import { FC, useState } from 'react';
import { Box, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { UsageFeature, UsageRangePreset, UsageService } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { CurrencyDollarIcon, LightningIcon, StackIcon, WarningIcon } from '@phosphor-icons/react';

import { useAdminTenantUsage } from '../../api/admin-usage-api';
import { AdminPanel, AdminPanelHead } from '../AdminPanel';
import { AdminTable, type AdminTableColumn } from '../AdminTable';
import { adminColors } from '../adminTheme';
import { StatCard } from '../StatCard';

import { UsageBreakdownPanel } from './UsageBreakdownPanel';
import {
  formatCompactCount,
  formatCount,
  formatDelta,
  formatUsd,
  formatUsdMicro,
} from './usageFormat';
import { UsageNumber } from './UsageNumber';
import { UsageRangePicker } from './UsageRangePicker';
import { UsageTrendChart } from './UsageTrendChart';

interface Props {
  tenantId: string;
}

/**
 * One tenant's cost breakdown.
 *
 * Rendered both as the Usage tab of the tenant detail page and (via the
 * leaderboard's row link) as the drill-down target. Keeping it a single
 * component means the two entry points can never drift.
 */
export const TenantUsagePanel: FC<Props> = observer(({ tenantId }) => {
  const { t } = useTrans();
  const theme = useTheme();
  const c = adminColors(theme);
  const [preset, setPreset] = useState<string>(UsageRangePreset.LAST_30D);

  const { data, isLoading, isError } = useAdminTenantUsage(tenantId, { preset });

  const featureLabel = (key: string) =>
    Object.values(UsageFeature).includes(key as never)
      ? t(`app.admin.usage.feature.${key}` as never)
      : key;
  const serviceLabel = (key: string) =>
    Object.values(UsageService).includes(key as never)
      ? t(`app.admin.usage.service.${key}` as never)
      : key;

  const delta = data ? formatDelta(data.totals.costUsd, data.totals.previousCostUsd) : null;

  const modelColumns: AdminTableColumn[] = [
    { key: 'model', label: t('app.admin.usage.byModel.col.model'), skeleton: 'title' },
    { key: 'calls', label: t('app.admin.usage.byModel.col.calls'), align: 'right', skeleton: 'number' },
    { key: 'in', label: t('app.admin.usage.byModel.col.in'), align: 'right', skeleton: 'number' },
    { key: 'out', label: t('app.admin.usage.byModel.col.out'), align: 'right', skeleton: 'number' },
    { key: 'cost', label: t('app.admin.usage.byModel.col.cost'), align: 'right', skeleton: 'number' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ fontSize: 12, color: c.faint }}>
          {t('app.admin.usage.range.tzLabel', { tz: data?.range.tz ?? '' })}
        </Typography>
        <UsageRangePicker value={preset} onChange={setPreset} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.75,
        }}
      >
        <StatCard
          icon={<CurrencyDollarIcon size={16} weight="bold" />}
          iconColor={c.green}
          label={t('app.admin.usage.stats.totalCost')}
          value={<UsageNumber>{data ? formatUsd(data.totals.costUsd) : '—'}</UsageNumber>}
          sub={delta ? t('app.admin.usage.stats.vsPrev', { delta }) : undefined}
        />
        <StatCard
          icon={<LightningIcon size={16} weight="bold" />}
          iconColor={c.azure}
          label={t('app.admin.usage.stats.calls')}
          value={<UsageNumber>{data ? formatCompactCount(data.totals.calls) : '—'}</UsageNumber>}
        />
        <StatCard
          icon={<StackIcon size={16} weight="bold" />}
          iconColor={c.violet}
          label={t('app.admin.usage.stats.tokens')}
          value={
            <UsageNumber>{data ? formatCompactCount(data.totals.totalTokens) : '—'}</UsageNumber>
          }
        />
        <StatCard
          icon={<WarningIcon size={16} weight="bold" />}
          iconColor={c.warn}
          label={t('app.admin.usage.stats.unpriced')}
          value={<UsageNumber>{data ? formatCount(data.unpriced.calls) : '—'}</UsageNumber>}
          sub={t('app.admin.usage.stats.unpricedSub')}
          accentBorder={data && data.unpriced.calls > 0 ? '#FFCCC7' : undefined}
        />
      </Box>

      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 1.75 }}
      >
        <UsageTrendChart
          points={data?.trend ?? []}
          isLoading={isLoading}
          title={t('app.admin.usage.trend.title')}
        />
        <UsageBreakdownPanel
          title={t('app.admin.usage.byFeature.title')}
          rows={data?.byFeature ?? []}
          isLoading={isLoading}
          labelFor={featureLabel}
          color={c.violet}
        />
      </Box>

      <UsageBreakdownPanel
        title={t('app.admin.usage.byService.title')}
        rows={data?.byService ?? []}
        isLoading={isLoading}
        labelFor={serviceLabel}
        color={c.teal}
      />

      <AdminPanel>
        <AdminPanelHead title={t('app.admin.usage.byModel.title')} />
        <AdminTable
          columns={modelColumns}
          isLoading={isLoading}
          isError={isError}
          errorLabel={t('app.admin.usage.topTenants.error')}
          isEmpty={!isLoading && (data?.byModel.length ?? 0) === 0}
          emptyLabel={t('app.admin.usage.byModel.empty')}
          skeletonRows={5}
        >
          {(data?.byModel ?? []).map((m) => (
            <TableRow key={`${m.service}:${m.model}`}>
              <TableCell>
                <Typography sx={{ fontSize: 13, color: c.ink }}>
                  {m.model || serviceLabel(m.service)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: c.faint }}>
                  {serviceLabel(m.service)}
                  {m.unpricedCalls > 0
                    ? ` · ${t('app.admin.usage.byModel.unpricedNote', {
                        calls: formatCount(m.unpricedCalls),
                      })}`
                    : ''}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <UsageNumber sx={{ fontSize: 13 }}>{formatCompactCount(m.calls)}</UsageNumber>
              </TableCell>
              <TableCell align="right">
                <UsageNumber sx={{ fontSize: 13 }}>
                  {formatCompactCount(m.promptTokens)}
                </UsageNumber>
              </TableCell>
              <TableCell align="right">
                <UsageNumber sx={{ fontSize: 13 }}>
                  {formatCompactCount(m.completionTokens)}
                </UsageNumber>
              </TableCell>
              <TableCell align="right">
                <UsageNumber sx={{ fontSize: 13, fontWeight: 600 }}>
                  {formatUsdMicro(m.costUsd)}
                </UsageNumber>
              </TableCell>
            </TableRow>
          ))}
        </AdminTable>
      </AdminPanel>
    </Box>
  );
});
